// src/pages/Apply/StepPayment.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import api from "../../utils/api";

const ENABLE_STRIPE = process.env.REACT_APP_ENABLE_STRIPE_PAYMENTS === "true";

const API = process.env.REACT_APP_API_URL;

const pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = pk ? loadStripe(pk) : null;

function PaymentForm({ app, onBack, onPaid }) {
  const token = localStorage.getItem("token");
  const stripe = useStripe();
  const elements = useElements();
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const alreadyPaid = app?.payment_status === "paid";

  useEffect(() => {
    if (alreadyPaid) {
      setMsg("✅ Payment already completed. Continue to review.");
    }
  }, [alreadyPaid]);

  const pay = async () => {
    setMsg("");
    if (alreadyPaid) {
      onPaid();
      return;
    }
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/auth/applications/${app.application_id}/pay-intent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { client_secret: clientSecret, status: piStatus } = r.data || {};
      if (!clientSecret) {
        setMsg("Payment error: no client secret.");
        setLoading(false);
        return;
      }

      if (piStatus === "succeeded") {
        await axios.get(
          `${API}/auth/applications/${app.application_id}/payment`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMsg("✅ Payment already completed.");
        onPaid();
        setLoading(false);
        return;
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) {
        setMsg(result.error.message || "Payment failed");
        setLoading(false);
        return;
      }

      await axios.get(
        `${API}/auth/applications/${app.application_id}/payment`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg("✅ Payment complete.");
      onPaid();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Payment error");
    } finally {
      setLoading(false);
    }
  };

  if (!ENABLE_STRIPE) {
    // 🔧 Payment disabled → just continue
    return (
      <div className="card">
	<p className="toast">
  ⚡ Payment is temporarily disabled. We’ll contact you regarding payment, or you can reach us at:   
                 <a href=" mailto:support@homefoodgrab.com">support@homefoodgrab.com</a>.
</p>

        <div className="actions">
          <button className="btn ghost" onClick={onBack}>Back</button>
          <button className="btn primary" onClick={onPaid}>Continue</button>
        </div>
      </div>
    );
  }

  if (!pk || !stripePromise) {
    return (
      <div className="card">
        <p className="toast error">
          Stripe is not configured. Add <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code>.
        </p>
        <div className="actions">
          <button className="btn ghost" onClick={onBack}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="row" style={{justifyContent:"space-between", alignItems:"baseline"}}>
        <p className="muted">Application fee: <b>£{(app.fee_amount || 2999) / 100}</b></p>
        <div className="muted">Step 4 / 5</div>
      </div>

      {alreadyPaid ? (
        <div className="toast">✅ Payment received. You can continue.</div>
      ) : (
        <div className="card stripe">
          <CardElement options={{ hidePostalCode: true }} />
        </div>
      )}

      {msg && <div className="toast">{msg}</div>}

      <div className="actions">
        <button className="btn ghost" onClick={onBack}>Back</button>
        <button className="btn" onClick={pay} disabled={loading || (!stripe && !alreadyPaid)}>
          {alreadyPaid ? "Continue" : loading ? "Processing…" : "Pay now"}
        </button>
      </div>
    </>
  );
}

export default function StepPayment({ app, onBack, onPaid }) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm app={app} onBack={onBack} onPaid={onPaid} />
    </Elements>
  );
}

